import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge, PriorityBadge } from "@/components/Badges";
import { Plus, Search, X } from "lucide-react";
import ContractDialog from "@/components/ContractDialog";
import { useAuth } from "@/contexts/AuthContext";
import { computeTotalWorkTimeAw } from "@/components/WorkItemRow";
import { useTimeFormat } from "@/hooks/useTimeFormat";

export default function Contracts() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const nav = useNavigate();
  const tf = useTimeFormat();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get("status") || "";
  const periodFilter = searchParams.get("period") || "";
  const fromParam = searchParams.get("from") || "";
  const toParam = searchParams.get("to") || "";
  const [contracts, setContracts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  const clearFilters = () => {
    const next = new URLSearchParams(searchParams);
    ["status", "period", "from", "to"].forEach((k) => next.delete(k));
    setSearchParams(next, { replace: true });
  };

  const load = async () => {
    const [c, cs, vs, es] = await Promise.all([
      api.get("/contracts"), api.get("/customers"),
      api.get("/vehicles"), api.get("/employees"),
    ]);
    setContracts(c.data); setCustomers(cs.data);
    setVehicles(vs.data); setEmployees(es.data);
  };
  useEffect(() => { load(); }, []);

  const customerById = useMemo(() => Object.fromEntries(customers.map((x) => [x.id, x])), [customers]);
  const vehicleById = useMemo(() => Object.fromEntries(vehicles.map((x) => [x.id, x])), [vehicles]);
  const employeeById = useMemo(() => Object.fromEntries(employees.map((x) => [x.id, x])), [employees]);

  function customerName(id) { return customerById[id]?.name || "—"; }
  function vehicleLabel(id) {
    const v = vehicleById[id];
    return v ? `${v.brand} ${v.model} · ${v.license_plate}` : "—";
  }
  function employeeName(id) {
    const e = employeeById[id];
    return e ? `${e.first_name} ${e.last_name}` : "";
  }

  const filtered = useMemo(() => {
    let base = contracts;
    // Status filter (Dashboard drill-through): supports single status or a
    // pipe-separated group like "waiting_parts|waiting_customer".
    if (statusFilter) {
      const allowed = new Set(statusFilter.split("|"));
      base = base.filter((c) => allowed.has(c.status));
    }
    // Period filter (Dashboard drill-through).  Compares against created_at
    // — same field the Dashboard uses to bucket its stats.
    if (fromParam || toParam) {
      base = base.filter((c) => {
        const ca = (c.created_at || "").slice(0, 10);
        if (!ca) return false;
        if (fromParam && ca < fromParam) return false;
        if (toParam && ca > toParam) return false;
        return true;
      });
    }
    const s = q.trim().toLowerCase();
    if (!s) return base;
    return base.filter((c) => {
      const cust = customerById[c.customer_id] || {};
      const veh = vehicleById[c.vehicle_id] || {};
      const haystack = [
        c.number, c.title, c.description, c.status, c.priority,
        c.planned_date, c.planned_time,
        cust.name, cust.phone, cust.email, cust.address,
        veh.brand, veh.model, veh.license_plate, veh.vin,
        veh.fuel_type, veh.engine,
        veh.mileage != null ? String(veh.mileage) : "",
        employeeName(c.assigned_employee_id),
        t(`status.${c.status}`, { defaultValue: "" }),
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(s);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contracts, customerById, vehicleById, employeeById, q, t, statusFilter, fromParam, toParam]);

  const canCreate = user?.role === "owner" || user?.role === "admin";

  return (
    <div className="space-y-6" data-testid="contracts-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500">{t("contracts.title")}</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold mt-1">{t("contracts.title")}</h1>
          {(statusFilter || fromParam || toParam) && (
            <div className="flex flex-wrap items-center gap-2 mt-2 text-xs" data-testid="contracts-active-filters">
              {statusFilter && (
                <span className="px-2 py-1 rounded bg-blue-500/15 border border-blue-500/30 text-blue-200">
                  {statusFilter.split("|").map((s) => t(`statuses.${s}`, { defaultValue: s })).join(" · ")}
                </span>
              )}
              {(fromParam || toParam) && (
                <span className="px-2 py-1 rounded bg-blue-500/15 border border-blue-500/30 text-blue-200 font-mono-stat">
                  {periodFilter ? `${t(`dashboard.period_${periodFilter}`, { defaultValue: periodFilter })}: ` : ""}
                  {fromParam || "…"} → {toParam || "…"}
                </span>
              )}
              <button type="button" onClick={clearFilters}
                className="inline-flex items-center gap-1 text-zinc-400 hover:text-zinc-200"
                data-testid="contracts-clear-filters">
                <X className="h-3 w-3" /> {t("common.clear_filters")}
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-2.5 text-zinc-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("common.search")}
              className="pl-9 bg-[#171B24] border-[#2E3241] w-64" data-testid="contracts-search" />
          </div>
          {canCreate && (
            <Button onClick={() => setOpen(true)} className="bg-blue-500 hover:bg-blue-600 text-white" data-testid="new-contract-btn">
              <Plus className="h-4 w-4 mr-1" /> {t("contracts.new_contract")}
            </Button>
          )}
        </div>
      </div>

      <div className="surface rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#2A2F3A] text-zinc-400">
            <tr>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-widest">{t("contracts.number")}</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-widest">{t("contracts.customer")}</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-widest">{t("contracts.vehicle")}</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-widest">{t("contracts.planned")}</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-widest">{t("contracts.working_time")}</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-widest">{t("common.priority")}</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-widest">{t("common.status")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={7} className="text-center text-zinc-500 py-8">{t("common.empty")}</td></tr>
            ) : filtered.map((c) => {
              const totalAw = computeTotalWorkTimeAw(c.work_items || []);
              return (
                <tr key={c.id} className="border-t border-[#2E3241] hover:bg-[#2A2F3A] cursor-pointer"
                    onClick={() => nav(`/contracts/${c.id}`)} data-testid={`contract-row-${c.number}`}>
                  <td className="px-4 py-3 font-display font-semibold text-blue-300">{c.number}</td>
                  <td className="px-4 py-3 text-zinc-200">{customerName(c.customer_id)}</td>
                  <td className="px-4 py-3 text-zinc-300">{vehicleLabel(c.vehicle_id)}</td>
                  <td className="px-4 py-3 text-zinc-400 font-mono-stat">{c.planned_date || "—"} {c.planned_time || ""}</td>
                  <td className="px-4 py-3 font-mono-stat text-zinc-300" data-testid={`contract-row-worktime-${c.number}`}>{tf.format(totalAw)}</td>
                  <td className="px-4 py-3"><PriorityBadge priority={c.priority} /></td>
                  <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ContractDialog
        open={open}
        onOpenChange={setOpen}
        onCreated={(c) => { load(); nav(`/contracts/${c.id}`); }}
      />
    </div>
  );
}
